import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';

export type AppMode = 'self-serve' | 'presenter';

export interface ChapterNavigation {
  previous?: () => void;
  next?: () => void;
}

interface AppModeValue {
  mode: AppMode;
  durationScale: number;
  registerChapterNavigation(navigation: ChapterNavigation): () => void;
}

const AppModeContext = createContext<AppModeValue | null>(null);

export function parseAppMode(search: string): AppMode {
  return new URLSearchParams(search).get('present') === '1' ? 'presenter' : 'self-serve';
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(target.tagName);
}

export function AppModeProvider({ children }: { children: ReactNode }) {
  const mode = parseAppMode(typeof window === 'undefined' ? '' : window.location.search);
  const navigationRef = useRef<ChapterNavigation>({});

  const registerChapterNavigation = useCallback((navigation: ChapterNavigation) => {
    navigationRef.current = navigation;
    return () => {
      if (navigationRef.current === navigation) navigationRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (mode !== 'presenter') return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) return;
      if (event.key === 'ArrowLeft' && navigationRef.current.previous) {
        event.preventDefault();
        navigationRef.current.previous();
      }
      if (event.key === 'ArrowRight' && navigationRef.current.next) {
        event.preventDefault();
        navigationRef.current.next();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [mode]);

  const value = useMemo<AppModeValue>(
    () => ({
      mode,
      durationScale: mode === 'presenter' ? 1.15 : 1,
      registerChapterNavigation,
    }),
    [mode, registerChapterNavigation],
  );

  return (
    <AppModeContext.Provider value={value}>
      <div data-mode={mode}>{children}</div>
    </AppModeContext.Provider>
  );
}

export function useAppMode(): AppModeValue {
  const value = useContext(AppModeContext);
  if (!value) throw new Error('useAppMode must be used inside AppModeProvider');
  return value;
}
